import type { ColumnDef } from "@tanstack/react-table";
import { Edit } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { DEFAULT_PRIMARY_COLUMN_WIDTHS } from "@/lib/column-defaults";
import type { AIFieldDefinition } from "@/types/deep-research-reports";
import type { UnifiedTableColumn } from "@/types/table-column";

// Convert UnifiedTableColumn to ColumnDef for tanstack table
export function getAIFieldColumnDefs(
  columns: UnifiedTableColumn<AIFieldDefinition>[],
  onEdit: (field: AIFieldDefinition) => void,
): ColumnDef<AIFieldDefinition>[] {
  return columns.map((col) => {
    const columnId = col.key as string;

    if (columnId === "actions") {
      return {
        id: columnId,
        header: col.title,
        meta: {
          title: col.title,
          sortable: col.sortable,
          filterable: col.filterable,
          required: col.required,
        },
        cell: ({ row }) => {
          const field = row.original;

          if (field.is_editable) {
            return (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => onEdit(field)}
                className="h-8 px-2"
              >
                <Edit className="h-4 w-4" />
              </Button>
            );
          } else {
            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled
                      className="h-8 px-2"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>
                    AI fields can't be edited after being submitted for
                    generation in a report
                  </p>
                </TooltipContent>
              </Tooltip>
            );
          }
        },
        size: col.size || 100,
        minSize: 80,
        maxSize: 120,
      };
    }

    return {
      id: columnId,
      accessorKey: columnId as keyof AIFieldDefinition,
      header: col.title,
      meta: {
        title: col.title,
        sortable: col.sortable,
        filterable: col.filterable,
        filterType: col.filterType,
        filterOptions: col.filterOptions,
        required: col.required,
      },
      cell: ({ row }) => {
        const value = row.getValue(columnId);

        // Special formatting for specific columns
        switch (columnId) {
          case "display_name":
            return (
              <div className="font-medium whitespace-normal break-words">
                {value as string}
              </div>
            );

          case "description":
            return (
              <div className="whitespace-normal break-words text-muted-foreground">
                {(value as string) || "-"}
              </div>
            );

          case "entity_type": {
            const typeMap: Record<string, string> = {
              account: "Account",
              opportunity: "Opportunity",
              person: "Person",
            };
            return (
              <Badge variant="secondary">
                {typeMap[value as string] || (value as string)}
              </Badge>
            );
          }

          case "content_type": {
            const field = row.original;
            const contentTypeMap: Record<string, string> = {
              pick_list: "Pick list",
              short_text: "Short text",
              long_text: "Long text",
            };

            if (field.content_type === "pick_list") {
              return <Badge variant="secondary">Pick list</Badge>;
            }

            return (
              <Badge variant="secondary">
                {contentTypeMap[field.content_type] || field.content_type}
              </Badge>
            );
          }

          case "active_report_count":
            return <div className="text-center">{(value as number) || 0}</div>;

          case "created_at": {
            const date = new Date(value as string);
            return (
              <div className="text-muted-foreground whitespace-nowrap">
                {date.toLocaleDateString()}
              </div>
            );
          }

          default:
            return (
              <div className="whitespace-normal break-words">
                {String(value || "")}
              </div>
            );
        }
      },
      size: col.size || 200,
      minSize: col.size ? Math.max(col.size * 0.6, 100) : 100,
      maxSize: col.size ? Math.min(col.size * 1.5, 500) : 500,
    };
  });
}

// Define the base columns structure
export function getAIFieldColumns(): UnifiedTableColumn<AIFieldDefinition>[] {
  return [
    {
      key: "display_name",
      title: "Field Name",
      required: true,
      width: "250px",
      size: DEFAULT_PRIMARY_COLUMN_WIDTHS.MEDIUM,
      sortable: true,
      filterable: false,
    },
    {
      key: "description",
      title: "Description",
      required: true,
      width: "350px",
      size: 350,
      sortable: true,
      filterable: true,
      filterType: "text",
    },
    {
      key: "entity_type",
      title: "Entity Type",
      required: true,
      width: "150px",
      size: 150,
      sortable: true,
      filterable: true,
      filterType: "select",
      filterOptions: [
        { label: "Account", id: "account" },
        { label: "Opportunity", id: "opportunity" },
        { label: "Person", id: "person" },
      ],
    },
    {
      key: "content_type",
      title: "Output Type",
      required: true,
      width: "150px",
      size: 150,
      sortable: true,
      filterable: true,
      filterType: "select",
      filterOptions: [
        { label: "Short text", id: "short_text" },
        { label: "Long text", id: "long_text" },
        { label: "Pick list", id: "pick_list" },
      ],
    },
    {
      key: "active_report_count",
      title: "Active Reports Used",
      required: true,
      width: "120px",
      size: 120,
      sortable: true,
      filterable: true,
      filterType: "number",
    },
    {
      key: "created_at",
      title: "Created",
      required: true,
      width: "120px",
      size: 120,
      sortable: true,
      filterable: true,
      filterType: "date",
    },
    {
      key: "actions",
      title: "Actions",
      required: true,
      width: "100px",
      size: 100,
      sortable: false,
      filterable: false,
    },
  ];
}
